import type {
  PortfolioDailyUpdate,
  PortfolioGroup,
  PortfolioInboxItem,
  PortfolioMemoryEntry,
} from '../components/portfolio/types'

interface PortfolioApiResponse<T> {
  success?: boolean
  requiresDispatch?: boolean
  dispatchConnected?: boolean
  error?: string
  count?: number
  items?: T[]
}

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_token=([^;]+)/)
  return match ? match[1] : ''
}

function mutationHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

async function parseApiResponse<T>(res: Response): Promise<T[]> {
  const text = await res.text()
  let payload: PortfolioApiResponse<T> | null = null

  try {
    payload = text ? (JSON.parse(text) as PortfolioApiResponse<T>) : null
  } catch {
    payload = null
  }

  if (!res.ok) {
    const error = payload?.error || text || `Request failed (${res.status})`
    throw new Error(error)
  }

  return Array.isArray(payload?.items) ? payload.items : []
}

export async function fetchPortfolioInbox(limit = 100): Promise<PortfolioInboxItem[]> {
  const res = await fetch(`/api/portfolio/inbox?limit=${limit}`, { credentials: 'include' })
  return parseApiResponse<PortfolioInboxItem>(res)
}

export async function fetchPortfolioUpdates(limit = 30): Promise<PortfolioDailyUpdate[]> {
  const res = await fetch(`/api/portfolio/updates?limit=${limit}`, { credentials: 'include' })
  return parseApiResponse<PortfolioDailyUpdate>(res)
}

export async function fetchPortfolioMemories(limit = 50): Promise<PortfolioMemoryEntry[]> {
  const res = await fetch(`/api/portfolio/memories?limit=${limit}`, { credentials: 'include' })
  return parseApiResponse<PortfolioMemoryEntry>(res)
}

export async function searchPortfolioMemories(query: string, limit = 50): Promise<PortfolioMemoryEntry[]> {
  const params = new URLSearchParams()
  params.set('limit', String(limit))
  if (query.trim()) params.set('q', query.trim())
  const res = await fetch(`/api/portfolio/memories/search?${params.toString()}`, { credentials: 'include' })
  return parseApiResponse<PortfolioMemoryEntry>(res)
}

export async function savePortfolioMemory(input: {
  title: string
  content: string
  summary?: string
  sourceType: string
  sourceId?: string
  tags?: string[]
  createdBy?: string
}): Promise<PortfolioMemoryEntry> {
  const res = await fetch('/api/portfolio/memories/save', {
    method: 'POST',
    credentials: 'include',
    headers: mutationHeaders(),
    body: JSON.stringify(input),
  })

  const text = await res.text()
  let payload: { error?: string; item?: PortfolioMemoryEntry } | null = null
  try {
    payload = text ? (JSON.parse(text) as { error?: string; item?: PortfolioMemoryEntry }) : null
  } catch {
    payload = null
  }

  if (!res.ok) {
    throw new Error(payload?.error || text || `Request failed (${res.status})`)
  }

  if (!payload?.item) {
    throw new Error('Memory save response missing item')
  }

  return payload.item
}

export async function fetchPortfolioGroups(): Promise<PortfolioGroup[]> {
  const res = await fetch('/api/portfolio/groups', { credentials: 'include' })
  return parseApiResponse<PortfolioGroup>(res)
}
